import { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { importApi, toolsApi, journalApi, runJournalBackfillUntilDone } from '@/lib/api/client';
import { formatCurrency } from '@/lib/formatters';
import { cn } from '@/lib/utils';

interface TransactionPreview {
  date: string;
  action: string;
  symbol: string;
  underlyingSymbol: string;
  optionType: string;
  strikePrice: number;
  expirationDate: string;
  quantity: number;
  price: number;
  fees: number;
  amount: number;
  valid: boolean;
  error?: string;
  groupId?: string;
  isStockTransaction?: boolean;
}

interface PreviewSummary {
  total: number;
  valid: number;
  invalid: number;
  opening: number;
  closing: number;
  expired: number;
  assigned?: number;
  exercised?: number;
  stockTransactions?: number;
  estimatedPositions: number;
}

interface ImportProgress {
  phase: 'parsing' | 'processing' | 'finalizing' | 'complete';
  current: number;
  total: number;
  chainsCreated: number;
  positionsCreated: number;
  positionsClosed: number;
  message?: string;
}

interface BatchProgress {
  currentBatch: number;
  totalBatches: number;
  transactionsProcessed: number;
  totalTransactions: number;
  chainsCreated: number;
  positionsCreated: number;
  positionsClosed: number;
  errors: string[];
  note?: string;
  status: 'idle' | 'importing' | 'complete' | 'error';
}

export interface ImportFollowUpsDeps {
  fixChainStatuses: () => Promise<unknown>;
  recalculatePnl: () => Promise<unknown>;
  backfillJournal: () => Promise<unknown>;
  rebuildStock: () => Promise<unknown>;
  reconcileStock: () => Promise<unknown>;
  onProgress?: (note: string) => void;
}

/**
 * Auto-finalize driver for a successful JSON import (Finding I3): fix-chain-statuses ->
 * recalculate-P&L -> journal-history backfill, in that order, each independently try/caught so
 * one failing never stops the next (mirrors `ToolsPage.tsx`'s `runReplayWithFollowUps`, which
 * this was extracted to match — same reasons: testable without a fetch mock, and a single place
 * naming the order rather than three call sites that could silently drift out of sync).
 *
 * The route behind this stream (`/import/json/execute-stream-v3`) now replays with
 * `capture: false`, which clears `position_events` for every chain it touches without capturing
 * new ones — journal backfill is not optional cleanup here, it's what gives imported chains any
 * journal history at all.
 *
 * `rebuildStock` (Task 16) runs after journal backfill — same reasoning and same ordering
 * as `ToolsPage.tsx`'s `runReplayWithFollowUps`: the battle-tested stock rebuild is its own
 * invocation, never folded into the import/replay call itself.
 *
 * `reconcileStock` (Task 17) runs LAST, after the stock rebuild — same reasoning as
 * `ToolsPage.tsx`'s `runReplayWithFollowUps`: force-matching against Schwab's live holdings is
 * its own invocation too, and catches corporate actions absent from `transactions` that a
 * rebuild-from-transactions cannot see.
 */
export async function runImportFollowUps(deps: ImportFollowUpsDeps): Promise<void> {
  deps.onProgress?.('Finalizing: closing expired positions...');
  try {
    await deps.fixChainStatuses();
  } catch (err) {
    console.warn('[import] fix-chain-statuses failed:', err);
  }

  deps.onProgress?.('Finalizing: recalculating P&L...');
  try {
    await deps.recalculatePnl();
  } catch (err) {
    console.warn('[import] recalculate-pnl failed:', err);
  }

  deps.onProgress?.('Finalizing: rebuilding journal history...');
  try {
    await deps.backfillJournal();
  } catch (err) {
    console.warn('[import] journal backfill failed:', err);
  }

  deps.onProgress?.('Finalizing: rebuilding stock positions...');
  try {
    await deps.rebuildStock();
  } catch (err) {
    console.warn('[import] rebuild-stock-positions failed:', err);
  }

  deps.onProgress?.('Finalizing: reconciling with Schwab...');
  try {
    await deps.reconcileStock();
  } catch (err) {
    console.warn('[import] reconcile-stock-positions failed:', err);
  }

  deps.onProgress?.('');
}

export function JsonImport() {
  const [jsonData, setJsonData] = useState<unknown>(null);
  const [fileName, setFileName] = useState<string>('');
  const [preview, setPreview] = useState<TransactionPreview[] | null>(null);
  const [summary, setSummary] = useState<PreviewSummary | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [importResult, setImportResult] = useState<{
    success: boolean;
    transactionsProcessed: number;
    chainsCreated: number;
    positionsCreated: number;
    positionsClosed: number;
    errors?: string[];
    note?: string;
    breakdown?: {
      expected: {
        total: number;
        opening: number;
        closing: number;
        expired: number;
        assigned: number;
        exercised: number;
        stockTransactions: number;
      };
      actual: {
        opening: number;
        closing: number;
        expired: number;
        assigned: number;
        exercised: number;
        stockTransactions: number;
        skippedDuplicates: number;
      };
    };
  } | null>(null);
  const [importProgress, setImportProgress] = useState<ImportProgress | null>(null);
  const [batchProgress, setBatchProgress] = useState<BatchProgress | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const previewMutation = useMutation({
    mutationFn: (json: unknown) => importApi.previewJson(json),
    onSuccess: (data) => {
      setPreview(data.transactions);
      setSummary(data.summary);
    },
  });

  const handleBatchedImport = useCallback(async (json: unknown) => {
    setIsImporting(true);
    setImportProgress(null);
    setImportResult(null);
    setBatchProgress(null);

    const progress: BatchProgress = {
      currentBatch: 1,
      totalBatches: 1,
      transactionsProcessed: 0,
      totalTransactions: (json as { BrokerageTransactions?: unknown[] }).BrokerageTransactions?.length || 0,
      chainsCreated: 0,
      positionsCreated: 0,
      positionsClosed: 0,
      errors: [],
      status: 'importing',
    };
    setBatchProgress({ ...progress });

    try {
      // Send all transactions in one call — the backend normalizes, sorts, and
      // processes them as a unit so closing orders can match opens from any point
      // in the dataset. SSE streaming provides real-time progress updates.
      const result = await importApi.executeJsonStream(json, (streamProgress) => {
        setImportProgress(streamProgress);
      });

      progress.transactionsProcessed = result.transactionsProcessed;
      progress.chainsCreated = result.chainsCreated;
      progress.positionsCreated = result.positionsCreated;
      progress.positionsClosed = result.positionsClosed;
      if (result.errors) {
        progress.errors.push(...result.errors);
      }

      // Auto-finalize: fix chain statuses + recalculate P&L + journal history backfill, each as
      // separate API calls. Each runs in its own Worker invocation to stay under D1 subrequest
      // limits — see runImportFollowUps's doc comment for why the journal step (Finding I3) is
      // not optional cleanup.
      if (result.transactionsProcessed > 0 && progress.errors.length === 0) {
        await runImportFollowUps({
          fixChainStatuses: toolsApi.fixChainStatuses,
          recalculatePnl: toolsApi.recalculatePnl,
          // Bounded per call (Finding I4) — loop until the server reports done.
          backfillJournal: () => runJournalBackfillUntilDone(journalApi.backfill),
          // Task 16: the battle-tested stock rebuild.
          rebuildStock: toolsApi.rebuildStockPositions,
          // Task 17: reconcile against Schwab's live holdings, run last — see
          // runImportFollowUps's doc comment.
          reconcileStock: toolsApi.reconcileStockPositions,
          onProgress: (note) => {
            progress.note = note || undefined;
            setBatchProgress({ ...progress });
          },
        });
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      progress.errors.push(errorMsg);
    }

    progress.status = progress.errors.length > 0 ? 'error' : 'complete';
    setBatchProgress({ ...progress });

    setImportResult({
      success: progress.errors.length === 0,
      transactionsProcessed: progress.transactionsProcessed,
      chainsCreated: progress.chainsCreated,
      positionsCreated: progress.positionsCreated,
      positionsClosed: progress.positionsClosed,
      errors: progress.errors.length > 0 ? progress.errors : undefined,
    });

    // Clear preview after import
    if (progress.errors.length === 0) {
      setPreview(null);
      setSummary(null);
    }

    setIsImporting(false);
    setImportProgress(null);
  }, []);

  const processFile = useCallback((file: File) => {
    setFileName(file.name);
    setPreview(null);
    setSummary(null);
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target?.result as string);
        setJsonData(json);
      } catch {
        alert('Invalid JSON file');
        setJsonData(null);
      }
    };
    reader.readAsText(file);
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processFile(file);
  }, [processFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file && file.name.endsWith('.json')) {
      processFile(file);
    } else {
      alert('Please drop a JSON file');
    }
  }, [processFile]);

  const handlePreview = () => {
    if (jsonData) {
      previewMutation.mutate(jsonData);
    }
  };

  const handleImport = () => {
    if (jsonData && confirm('Import these transactions? This will create/update chains and positions.')) {
      handleBatchedImport(jsonData);
    }
  };

  const handleReset = () => {
    setJsonData(null);
    setFileName('');
    setPreview(null);
    setSummary(null);
    setImportResult(null);
    setBatchProgress(null);
  };

  // Group transactions by groupId for display
  const groupedTransactions = preview?.reduce((groups, txn) => {
    const key = txn.groupId || 'ungrouped';
    if (!groups[key]) groups[key] = [];
    groups[key].push(txn);
    return groups;
  }, {} as Record<string, TransactionPreview[]>);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Schwab JSON Import</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-gray-600 space-y-2">
          <p>
            Export your transaction history from Schwab as JSON and import it here.
            This will automatically:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Group multi-leg trades (iron condors, spreads)</li>
            <li>Detect rolls and link them to existing chains</li>
            <li>Process closing orders and expirations</li>
            <li>Handle partial closes</li>
          </ul>
        </div>

        {/* File Upload Drop Zone */}
        <div
          className={cn(
            "border-3 border-dashed rounded-xl p-8 text-center transition-all duration-200",
            "bg-gradient-to-b from-gray-50 to-white",
            isDragging
              ? "border-brand-500 bg-brand-50 scale-[1.02] shadow-lg"
              : "border-gray-300 hover:border-brand-400 hover:bg-gray-50"
          )}
          style={{ borderWidth: '3px' }}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept=".json"
            onChange={handleFileChange}
            className="hidden"
            id="json-file-input"
          />
          <label
            htmlFor="json-file-input"
            className="cursor-pointer block"
          >
            <div className="space-y-3">
              <div className={cn(
                "mx-auto w-16 h-16 rounded-full flex items-center justify-center transition-colors",
                isDragging ? "bg-brand-100" : "bg-gray-100"
              )}>
                <svg
                  className={cn(
                    "h-8 w-8 transition-colors",
                    isDragging ? "text-brand-600" : "text-gray-400"
                  )}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </div>
              <p className={cn(
                "text-base font-medium",
                isDragging ? "text-brand-600" : "text-gray-600"
              )}>
                {fileName ? (
                  <span className="font-medium text-brand-600">{fileName}</span>
                ) : (
                  <>
                    <span className="font-medium text-brand-600">Click to upload</span>
                    {' '}or drag and drop
                  </>
                )}
              </p>
              <p className="text-xs text-gray-500">JSON file exported from Schwab</p>
            </div>
          </label>
        </div>

        {/* Preview Button */}
        {jsonData !== null && !preview && (
          <div className="flex justify-end">
            <Button
              onClick={handlePreview}
              disabled={previewMutation.isPending}
            >
              {previewMutation.isPending ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Analyzing...
                </>
              ) : (
                'Preview Import'
              )}
            </Button>
          </div>
        )}

        {/* Preview Error */}
        {previewMutation.isError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">
              {previewMutation.error instanceof Error
                ? previewMutation.error.message
                : 'Failed to preview import'}
            </p>
          </div>
        )}

        {/* Summary */}
        {summary && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">Import Summary</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Total Transactions</span>
                <p className="font-medium">{summary.total}</p>
              </div>
              <div>
                <span className="text-gray-500">Opening</span>
                <p className="font-medium text-green-600">{summary.opening}</p>
              </div>
              <div>
                <span className="text-gray-500">Closing</span>
                <p className="font-medium text-blue-600">{summary.closing}</p>
              </div>
              <div>
                <span className="text-gray-500">Expired</span>
                <p className="font-medium text-gray-600">{summary.expired}</p>
              </div>
              {(summary.assigned ?? 0) > 0 && (
                <div>
                  <span className="text-gray-500">Assigned</span>
                  <p className="font-medium text-amber-600">{summary.assigned}</p>
                </div>
              )}
              {(summary.exercised ?? 0) > 0 && (
                <div>
                  <span className="text-gray-500">Exercised</span>
                  <p className="font-medium text-purple-600">{summary.exercised}</p>
                </div>
              )}
              {(summary.stockTransactions ?? 0) > 0 && (
                <div>
                  <span className="text-gray-500">Stock (Assign/Exer)</span>
                  <p className="font-medium text-indigo-600">{summary.stockTransactions}</p>
                </div>
              )}
              {summary.invalid > 0 && (
                <div>
                  <span className="text-gray-500">Invalid</span>
                  <p className="font-medium text-red-600">{summary.invalid}</p>
                </div>
              )}
              <div>
                <span className="text-gray-500">Est. Positions</span>
                <p className="font-medium">{summary.estimatedPositions}</p>
              </div>
            </div>
          </div>
        )}

        {/* Transaction Preview */}
        {preview && groupedTransactions && (
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Transactions</h4>
            <div className="max-h-96 overflow-y-auto border rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Action</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Symbol</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Strike</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Exp</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Qty</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Price</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {Object.entries(groupedTransactions).map(([groupId, txns]) => (
                    txns.map((txn, idx) => (
                      <tr
                        key={`${groupId}-${idx}`}
                        className={cn(
                          !txn.valid && 'bg-red-50',
                          idx === 0 && groupId !== 'ungrouped' && 'border-t-2 border-gray-300'
                        )}
                      >
                        <td className="px-3 py-2 whitespace-nowrap">{txn.date}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={cn(
                            'px-2 py-0.5 rounded text-xs font-medium',
                            txn.action === 'BTO' && 'bg-green-100 text-green-700',
                            txn.action === 'STO' && 'bg-blue-100 text-blue-700',
                            txn.action === 'BTC' && 'bg-orange-100 text-orange-700',
                            txn.action === 'STC' && 'bg-purple-100 text-purple-700',
                            txn.action === 'EXPIRED' && 'bg-gray-100 text-gray-700',
                            txn.action === 'ASSIGNED' && 'bg-amber-100 text-amber-700',
                            txn.action === 'EXERCISED' && 'bg-purple-100 text-purple-700',
                            txn.action === 'BUY_STOCK' && 'bg-indigo-100 text-indigo-700',
                            txn.action === 'SELL_STOCK' && 'bg-indigo-100 text-indigo-700'
                          )}>
                            {txn.action}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap font-medium">
                          {txn.underlyingSymbol}
                          {txn.optionType !== 'stock' && (
                            <span className="ml-1 text-gray-500 text-xs">
                              {txn.optionType === 'call' ? 'C' : 'P'}
                            </span>
                          )}
                          {txn.isStockTransaction && (
                            <span className="ml-1 text-gray-500 text-xs">stock</span>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {txn.isStockTransaction ? `@$${txn.price.toFixed(2)}` : `$${txn.strikePrice}`}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                          {txn.expirationDate || '-'}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right">{txn.quantity}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-right">${txn.price.toFixed(2)}</td>
                        <td className={cn(
                          'px-3 py-2 whitespace-nowrap text-right font-medium',
                          txn.amount >= 0 ? 'text-green-600' : 'text-red-600'
                        )}>
                          {formatCurrency(txn.amount)}
                        </td>
                      </tr>
                    ))
                  ))}
                </tbody>
              </table>
            </div>

            {/* Batch Import Progress */}
            {batchProgress && batchProgress.status === 'importing' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Spinner size="sm" />
                  <span className="font-medium text-blue-900">
                    Importing batch {batchProgress.currentBatch} of {batchProgress.totalBatches}...
                  </span>
                </div>

                {/* Batch Progress bar */}
                <div className="mb-3">
                  <div className="flex justify-between text-sm text-blue-700 mb-1">
                    <span>Overall Progress</span>
                    <span>{batchProgress.transactionsProcessed} / {batchProgress.totalTransactions} transactions</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${batchProgress.totalTransactions > 0 ? (batchProgress.transactionsProcessed / batchProgress.totalTransactions) * 100 : 0}%` }}
                    />
                  </div>
                </div>

                {/* Current batch progress */}
                {importProgress && (
                  <div className="mb-3 pl-4 border-l-2 border-blue-300">
                    <div className="flex justify-between text-xs text-blue-600 mb-1">
                      <span>Current Batch</span>
                      <span>{importProgress.current} / {importProgress.total}</span>
                    </div>
                    <div className="w-full bg-blue-100 rounded-full h-1">
                      <div
                        className="bg-blue-400 h-1 rounded-full transition-all duration-300"
                        style={{ width: `${importProgress.total > 0 ? (importProgress.current / importProgress.total) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-blue-600">Chains Created</span>
                    <p className="font-medium text-blue-900">{batchProgress.chainsCreated}</p>
                  </div>
                  <div>
                    <span className="text-blue-600">Positions Created</span>
                    <p className="font-medium text-blue-900">{batchProgress.positionsCreated}</p>
                  </div>
                  <div>
                    <span className="text-blue-600">Positions Closed</span>
                    <p className="font-medium text-blue-900">{batchProgress.positionsClosed}</p>
                  </div>
                </div>

                {/* Show errors as they occur */}
                {batchProgress.errors.length > 0 && (
                  <div className="mt-3 text-xs text-amber-700 bg-amber-50 p-2 rounded">
                    {batchProgress.errors.length} error(s) so far - will continue with remaining batches
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={handleReset} disabled={isImporting}>
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                disabled={isImporting || (summary?.valid || 0) === 0}
              >
                {isImporting ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Importing...
                  </>
                ) : (
                  `Import ${summary?.valid || 0} Transactions`
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Import Result */}
        {importResult && (
          <div className={cn(
            'rounded-lg p-4',
            importResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          )}>
            <h4 className={cn(
              'font-medium mb-2',
              importResult.success ? 'text-green-900' : 'text-red-900'
            )}>
              {importResult.success ? 'Import Complete!' : 'Import Completed with Errors'}
            </h4>

            {/* Breakdown comparison */}
            {importResult.breakdown && (
              <div className="mb-4 bg-white rounded border p-3">
                <h5 className="font-medium text-gray-700 mb-2 text-sm">Transaction Breakdown</h5>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="font-medium text-gray-500">Type</div>
                  <div className="font-medium text-gray-500 text-right">Expected</div>
                  <div className="font-medium text-gray-500 text-right">Actual</div>

                  <div>Opening</div>
                  <div className="text-right">{importResult.breakdown.expected.opening}</div>
                  <div className={cn(
                    'text-right',
                    importResult.breakdown.actual.opening !== importResult.breakdown.expected.opening && 'text-amber-600 font-medium'
                  )}>{importResult.breakdown.actual.opening}</div>

                  <div>Closing</div>
                  <div className="text-right">{importResult.breakdown.expected.closing}</div>
                  <div className={cn(
                    'text-right',
                    importResult.breakdown.actual.closing !== importResult.breakdown.expected.closing && 'text-amber-600 font-medium'
                  )}>{importResult.breakdown.actual.closing}</div>

                  <div>Expired</div>
                  <div className="text-right">{importResult.breakdown.expected.expired}</div>
                  <div className={cn(
                    'text-right',
                    importResult.breakdown.actual.expired !== importResult.breakdown.expected.expired && 'text-amber-600 font-medium'
                  )}>{importResult.breakdown.actual.expired}</div>

                  <div>Assigned</div>
                  <div className="text-right">{importResult.breakdown.expected.assigned}</div>
                  <div className={cn(
                    'text-right',
                    importResult.breakdown.actual.assigned !== importResult.breakdown.expected.assigned && 'text-amber-600 font-medium'
                  )}>{importResult.breakdown.actual.assigned}</div>

                  <div>Exercised</div>
                  <div className="text-right">{importResult.breakdown.expected.exercised}</div>
                  <div className={cn(
                    'text-right',
                    importResult.breakdown.actual.exercised !== importResult.breakdown.expected.exercised && 'text-amber-600 font-medium'
                  )}>{importResult.breakdown.actual.exercised}</div>

                  <div>Stock (Assign/Exer)</div>
                  <div className="text-right">{importResult.breakdown.expected.stockTransactions}</div>
                  <div className={cn(
                    'text-right',
                    importResult.breakdown.actual.stockTransactions !== importResult.breakdown.expected.stockTransactions && 'text-amber-600 font-medium'
                  )}>{importResult.breakdown.actual.stockTransactions}</div>

                  {importResult.breakdown.actual.skippedDuplicates > 0 && (
                    <>
                      <div className="text-gray-400">Skipped (duplicates)</div>
                      <div className="text-right text-gray-400">-</div>
                      <div className="text-right text-gray-400">{importResult.breakdown.actual.skippedDuplicates}</div>
                    </>
                  )}

                  <div className="font-medium border-t pt-1 mt-1">Total</div>
                  <div className="text-right font-medium border-t pt-1 mt-1">{importResult.breakdown.expected.total}</div>
                  <div className={cn(
                    'text-right font-medium border-t pt-1 mt-1',
                    importResult.transactionsProcessed !== importResult.breakdown.expected.total && 'text-amber-600'
                  )}>{importResult.transactionsProcessed}</div>
                </div>
              </div>
            )}

            <div className="text-sm space-y-1">
              <p>Transactions processed: {importResult.transactionsProcessed}</p>
              <p>Chains created: {importResult.chainsCreated}</p>
              <p>Positions created: {importResult.positionsCreated}</p>
              <p>Positions closed: {importResult.positionsClosed}</p>
              {importResult.note && (
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-blue-700 text-xs">
                  {importResult.note}
                </div>
              )}
              {importResult.errors && importResult.errors.length > 0 && (
                <div className="mt-2">
                  <p className="font-medium text-red-700">Errors:</p>
                  <ul className="list-disc list-inside text-red-600">
                    {importResult.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="mt-4">
              <Button variant="secondary" onClick={handleReset}>
                Import Another File
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
